import { useMemo, useState } from 'react'
import { DataFreshnessBadge } from '../components/DataFreshnessBadge'
import { MetricStrip } from '../components/MetricStrip'
import { TrendChart } from '../components/TrendChart'
import { jdOrderWindowMetrics } from '../jdOrders'
import { JdOrderStatisticsBar, jdOrderStatisticsWindow, useJdOrderStatisticsPeriod } from '../jdOrderStatistics'
import { useJdAfterSalesFacts, useJdOrderFacts } from '../platform'
import { integer, money } from '../utils'

type TrendKey = 'completedReceivable' | 'fulfillingReceivable' | 'cancelledReceivable' | 'successfulRefundAmount' | 'completedOrderRefundAmount' | 'netCompletedReceivable'

const trendOptions: Record<TrendKey, { label: string; formatter: (value: number) => string }> = {
  completedReceivable: { label: '完成订单金额', formatter: money },
  fulfillingReceivable: { label: '履约中金额', formatter: money },
  cancelledReceivable: { label: '支付后取消', formatter: money },
  successfulRefundAmount: { label: '售后退款成功', formatter: money },
  completedOrderRefundAmount: { label: '完成后退款', formatter: money },
  netCompletedReceivable: { label: '净成交参考', formatter: money },
}

export function JdOrdersPage() {
  const orderFacts = useJdOrderFacts()
  const afterSalesFacts = useJdAfterSalesFacts()
  const [period, setPeriod] = useJdOrderStatisticsPeriod('last7')
  const [trendKey, setTrendKey] = useState<TrendKey>('completedReceivable')
  const window = useMemo(
    () => jdOrderStatisticsWindow(orderFacts.data?.meta, period),
    [orderFacts.data?.meta, period],
  )
  const metrics = useMemo(() => {
    if (!orderFacts.data || !window) return null
    return jdOrderWindowMetrics(orderFacts.data, afterSalesFacts.data || null, window.startDate, window.endDate)
  }, [afterSalesFacts.data, orderFacts.data, window])
  const refundInWindow = useMemo(
    () => (metrics?.daily || []).reduce((sum, row) => sum + row.successfulRefundAmount, 0),
    [metrics],
  )
  const trendData = useMemo(() => (metrics?.daily || []).flatMap((row) => {
    const value = row[trendKey]
    return value == null ? [] : [{ date: row.date, value }]
  }), [metrics, trendKey])

  if (orderFacts.loading || afterSalesFacts.loading) return <div className="loading-state">正在加载京东订单口径数据…</div>
  if (orderFacts.error || !orderFacts.data) return (
    <div className="error-state">
      <h1>京东订单数据加载失败</h1>
      <p>{orderFacts.error || '缺少 jd-order-facts.json，请先运行 import_jd_orders.py'}</p>
      <button type="button" className="primary-button" onClick={() => void orderFacts.reload()}>重试</button>
    </div>
  )
  const meta = orderFacts.data.meta
  const afterSalesMeta = afterSalesFacts.data?.meta
  const trendOption = trendOptions[trendKey]
  const lockedNote = metrics && metrics.locked.orderCount > 0 ? ` · 含售后锁定 ${integer(metrics.locked.orderCount)} 单` : ''

  return (
    <div className="page-stack pdd-page">
      <div className="page-title-row">
        <div>
          <span className="pdd-platform-label">JD</span>
          <h1>订单口径</h1>
          <p>{meta.storeName} · 订单明细导出 {meta.queryStart} 至 {meta.queryEnd}</p>
        </div>
        <div className="page-title-actions">
          <DataFreshnessBadge date={meta.queryEnd} label="订单数据截至" mode="on-demand" />
          {afterSalesMeta ? <DataFreshnessBadge date={afterSalesMeta.queryEnd} label="售后数据截至" mode="on-demand" /> : null}
        </div>
      </div>

      <div className="pdd-caliber-note">
        <strong>口径边界：</strong>本页来自店铺后台订单明细与售后服务单两份导出，与商智页面的成交金额口径不同。订单按付款日期归日、金额为订单级商家应收（{integer(meta.provisionalReceivableOrders)} 单仍为系统暂估）；完成＝订单状态「完成」，履约中＝等待出库、等待确认收货、调度中等在途已支付，支付后取消＝「(删除)」前缀。售后按申请日期归日，退款只计服务单状态「完成」。净成交参考＝完成订单金额－完成后服务单退款（退款对应订单仍显示「完成」的部分）；两者时间轴不同（付款日 vs 申请日），故为参考值。跨期退款（退更早订单）单列不进净成交；订单已取消的退款不再扣减，避免与取消口径重复。订单状态为导出时点快照，会随售后与取消回溯变动。
      </div>

      <JdOrderStatisticsBar period={period} onChange={setPeriod} window={window} />

      {!metrics || !window ? <div className="loading-state">正在计算统计窗口…</div> : <>
        <MetricStrip items={[
          { label: '完成订单金额', value: money(metrics.completed.merchantReceivable), context: `${integer(metrics.completed.orderCount)}单 · 商家应收口径`, active: trendKey === 'completedReceivable', onClick: () => setTrendKey('completedReceivable') },
          { label: '履约中金额', value: money(metrics.fulfilling.merchantReceivable + metrics.locked.merchantReceivable), context: `${integer(metrics.fulfilling.orderCount + metrics.locked.orderCount)}单在途${lockedNote}`, active: trendKey === 'fulfillingReceivable', onClick: () => setTrendKey('fulfillingReceivable') },
          { label: '支付后取消', value: money(metrics.cancelled.merchantReceivable), context: `${integer(metrics.cancelled.orderCount)}单 · 原商家应收`, active: trendKey === 'cancelledReceivable', onClick: () => setTrendKey('cancelledReceivable') },
          { label: '售后退款成功', value: afterSalesFacts.data ? money(refundInWindow) : '—', context: '按申请日 · 含取消与跨期退款', active: trendKey === 'successfulRefundAmount', onClick: () => setTrendKey('successfulRefundAmount') },
          { label: '完成后退款', value: metrics.linkageAvailable ? money(metrics.completedOrderRefundAmount) : '—', context: metrics.linkageAvailable ? `${integer(metrics.completedOrderRefundCount)}单 · 订单仍显示完成` : '缺少订单勾稽', active: trendKey === 'completedOrderRefundAmount', onClick: () => setTrendKey('completedOrderRefundAmount') },
          { label: '跨期退款', value: metrics.linkageAvailable ? money(metrics.outsideRefundAmount) : '—', context: metrics.linkageAvailable ? `${integer(metrics.outsideRefundCount)}单 · 退更早订单，不进净成交` : '缺少订单勾稽' },
          { label: '净成交参考', value: metrics.linkageAvailable ? money(metrics.netCompletedReceivable) : '—', context: '完成订单金额－完成后退款', active: trendKey === 'netCompletedReceivable', onClick: () => setTrendKey('netCompletedReceivable') },
        ]} />

        <section className="overview-section pdd-trend-section">
          <div className="section-heading-row"><div><h2>{window.label}{trendOption.label}每日趋势</h2><p>点击上方指标卡切换；订单类按付款日、售后类按申请日</p></div></div>
          <TrendChart data={trendData} valueFormatter={trendOption.formatter} valueLabel={trendOption.label} ariaLabel={`京东${window.label}${trendOption.label}每日趋势`} />
          <div className="pdd-trend-footnote">完成后退款与净成交参考依赖售后导出附带的订单勾稽；订单已取消的退款不重复扣减。</div>
        </section>

        <section className="overview-section">
          <div className="section-heading-row"><div><h2>每日明细</h2><p>{window.startDate} 至 {window.endDate}</p></div></div>
          <div className="table-responsive">
            <table className="pdd-table">
              <thead>
                <tr>
                  <th>日期</th>
                  <th>完成单数</th>
                  <th>完成金额</th>
                  <th>履约中</th>
                  <th>支付后取消</th>
                  <th>售后申请</th>
                  <th>退款成功</th>
                  <th>完成后退款</th>
                  <th>净成交参考</th>
                </tr>
              </thead>
              <tbody>
                {metrics.daily.map((row) => (
                  <tr key={row.date}>
                    <td>{row.date}</td>
                    <td>{integer(row.completedOrderCount)}</td>
                    <td>{money(row.completedReceivable)}</td>
                    <td>{money(row.fulfillingReceivable)}</td>
                    <td>{money(row.cancelledReceivable)}</td>
                    <td>{integer(row.applicationCount)}</td>
                    <td>{money(row.successfulRefundAmount)}</td>
                    <td>{row.completedOrderRefundAmount == null ? '—' : money(row.completedOrderRefundAmount)}</td>
                    <td>{row.netCompletedReceivable == null ? '—' : money(row.netCompletedReceivable)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </>}
    </div>
  )
}
